import { doc, onSnapshot, setDoc } from "firebase/firestore";
import { firestore } from "../../../configs/FirebaseConfig";
import {
  FIRESTORE_REGIONS_TABLE,
  FIRESTORE_SETTINGS_TABLE,
  FIRESTORE_WHATSAPP_TEMPLATES_DOC,
} from "../../../constants/FirestoreConstant";
import { message } from "antd";
import { useCallback, useEffect, useState } from "react";

const NL_REGION = "NL";

export function useWhatsappTemplates() {
  const [data, setData] = useState(undefined);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const docRef = doc(
      firestore,
      FIRESTORE_REGIONS_TABLE,
      NL_REGION,
      FIRESTORE_SETTINGS_TABLE,
      FIRESTORE_WHATSAPP_TEMPLATES_DOC,
    );

    const unsubscribe = onSnapshot(
      docRef,
      (snapshot) => {
        try {
          if (snapshot.exists()) {
            const docData = snapshot.data();
            const templates = docData?.templates || [];
            setData(templates);
            setError(null);
            setLoading(false);
          } else {
            setData([]);
            setError(null);
            setLoading(false);
          }
        } catch (err) {
          console.error("Error in WhatsApp templates snapshot:", err);
          setError(err);
          setLoading(false);
        }
      },
      (err) => {
        console.error("Error subscribing to WhatsApp templates:", err);
        setError(err);
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, []);

  const saveTemplates = useCallback(async (templates) => {
    try {
      const docRef = doc(
        firestore,
        FIRESTORE_REGIONS_TABLE,
        NL_REGION,
        FIRESTORE_SETTINGS_TABLE,
        FIRESTORE_WHATSAPP_TEMPLATES_DOC,
      );

      await setDoc(docRef, {
        templates,
        updatedAt: new Date().toISOString(),
      });

      return { success: true };
    } catch (err) {
      console.error("Error saving WhatsApp templates:", err);
      message.error("Failed to save templates");
      return { success: false, error: err };
    }
  }, []);

  const addTemplate = useCallback(
    async (template) => {
      try {
        const currentTemplates = data || [];
        const newTemplate = {
          ...template,
          id: template.id || `template_${Date.now()}`,
          variables: template.variables || [],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        const updatedTemplates = [...currentTemplates, newTemplate];
        await saveTemplates(updatedTemplates);
        message.success("Template added successfully");
        return { success: true, template: newTemplate };
      } catch (err) {
        console.error("Error adding template:", err);
        message.error("Failed to add template");
        return { success: false, error: err };
      }
    },
    [data, saveTemplates],
  );

  const updateTemplate = useCallback(
    async (templateId, updates) => {
      try {
        const currentTemplates = data || [];
        const updatedTemplates = currentTemplates.map((t) =>
          t.id === templateId
            ? {
                ...t,
                ...updates,
                variables: updates.variables || [],
                updatedAt: new Date().toISOString(),
              }
            : t,
        );

        await saveTemplates(updatedTemplates);
        message.success("Template updated successfully");
        return { success: true };
      } catch (err) {
        console.error("Error updating template:", err);
        message.error("Failed to update template");
        return { success: false, error: err };
      }
    },
    [data, saveTemplates],
  );

  const deleteTemplate = useCallback(
    async (templateId) => {
      try {
        const currentTemplates = data || [];
        const updatedTemplates = currentTemplates.filter((t) => t.id !== templateId);

        await saveTemplates(updatedTemplates);
        message.success("Template deleted successfully");
        return { success: true };
      } catch (err) {
        console.error("Error deleting template:", err);
        message.error("Failed to delete template");
        return { success: false, error: err };
      }
    },
    [data, saveTemplates],
  );

  const toggleTemplateActive = useCallback(
    async (templateId) => {
      try {
        const currentTemplates = data || [];
        const template = currentTemplates.find((t) => t.id === templateId);
        if (!template) {
          throw new Error("Template not found");
        }

        await updateTemplate(templateId, { active: !template.active });
        return { success: true };
      } catch (err) {
        console.error("Error toggling template:", err);
        message.error("Failed to toggle template");
        return { success: false, error: err };
      }
    },
    [data, updateTemplate],
  );

  return {
    data: data || [],
    error,
    loading,
    addTemplate,
    updateTemplate,
    deleteTemplate,
    toggleTemplateActive,
    saveTemplates,
  };
}
